import React, { useState, useRef, useEffect } from 'react';
import { FaSearch, FaTimes, FaChevronDown } from 'react-icons/fa';
import axios from 'axios';

interface Company {
  id: number;
  name: string;
  business_name?: string;
}

interface CompanySearchProps {
  selectedCompany: Company | null;
  onCompanySelect: (company: Company) => void;
  onClearSelection: () => void;
}

const CompanySearch: React.FC<CompanySearchProps> = ({ 
  selectedCompany, 
  onCompanySelect, 
  onClearSelection 
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isCompanyPickerOpen, setIsCompanyPickerOpen] = useState<boolean>(false);
  const [companiesData, setCompaniesData] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<any>(null);
  const [defaultCompanies, setDefaultCompanies] = useState<Company[]>([]);
  const [defaultError, setDefaultError] = useState<any>(null);
  const companyPickerRef = useRef<HTMLDivElement>(null);

  // Load default companies when component mounts
  useEffect(() => {
    const loadDefaultCompanies = async () => {
      try {
        console.log('📡 Loading default companies...');
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/businesses`, {
          params: { limit: 20 }
        });
        
        console.log('📥 Default companies response:', response.data);
        
        let companies = [];
        
        if (Array.isArray(response.data)) {
          companies = response.data;
        } else if (response.data && Array.isArray(response.data.businesses)) {
          companies = response.data.businesses;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          companies = response.data.data;
        } else {
          console.warn('⚠️ Unexpected default companies response format:', response.data);
          companies = [];
        }
        
        console.log('🏢 Default companies processed:', companies);
        setDefaultCompanies(companies);
      } catch (error) {
        console.error('❌ Default companies error:', error);
        setDefaultError(error);
      }
    };

    loadDefaultCompanies();
  }, []);

  // Search companies when searchTerm changes
  useEffect(() => {
    const searchCompanies = async () => {
      if (!searchTerm || searchTerm.trim().length < 1) {
        console.log('🔍 No search term, clearing results');
        setCompaniesData([]);
        setSearchError(null);
        return;
      }
      
      console.log('🔍 Searching for companies with term:', searchTerm);
      setIsLoadingCompanies(true);
      setSearchError(null);
      
      try {
        const searchParams = { 
          search: searchTerm.trim(),
          limit: 20 
        };
        
        console.log('📡 API call params:', searchParams);
        console.log('📡 API URL:', `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/businesses`);
        
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/businesses`, {
          params: searchParams
        });
        
        console.log('📥 Search response status:', response.status);
        console.log('📥 Search response data:', response.data);
        console.log('📥 Response type:', typeof response.data);
        console.log('📥 Is array:', Array.isArray(response.data));
        
        let companies = [];
        
        if (Array.isArray(response.data)) {
          companies = response.data;
        } else if (response.data && Array.isArray(response.data.businesses)) {
          companies = response.data.businesses;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          companies = response.data.data;
        } else {
          console.warn('⚠️ Unexpected response format:', response.data);
          companies = [];
        }
        
        console.log('🏢 Processed companies:', companies);
        
        // Always filter results to ensure we get what we're looking for
        if (searchTerm.trim().length > 0) {
          const filtered = companies.filter((company: Company) => {
            const searchLower = searchTerm.toLowerCase();
            const name = (company.name || '').toLowerCase();
            const businessName = (company.business_name || '').toLowerCase();
            const id = company.id.toString();
            
            const nameMatch = name.includes(searchLower);
            const businessNameMatch = businessName.includes(searchLower);
            const idMatch = id.includes(searchTerm);
            
            console.log(`🔍 Company ${company.id}: name="${name}" business="${businessName}" id="${id}"`);
            console.log(`🔍 Matches: name=${nameMatch} business=${businessNameMatch} id=${idMatch}`);
            
            return nameMatch || businessNameMatch || idMatch;
          });
          
          console.log('🔍 Filtered results:', filtered);
          setCompaniesData(filtered);
        } else {
          setCompaniesData(companies);
        }
      } catch (error) {
        console.error('❌ Company search error:', error);
        setSearchError(error);
        if (axios.isAxiosError(error)) {
          console.error('❌ Axios error details:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
            url: error.config?.url,
            params: error.config?.params
          });
        }
        setCompaniesData([]);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    // Debounce the search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      searchCompanies();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Company picker functions
  const handleCompanySelect = (company: Company): void => {
    console.log('✅ Selected company:', company);
    onCompanySelect(company);
    setSearchTerm(company.name || company.business_name || '');
    setIsCompanyPickerOpen(false);
  };

  const handleCompanySearch = (search: string): void => {
    console.log('🔍 Search input changed to:', search);
    setSearchTerm(search);
    
    if (search.trim().length >= 1) {
      setIsCompanyPickerOpen(true);
      console.log('🔍 Opening dropdown and triggering search for:', search);
    } else if (search.trim().length === 0 && defaultCompanies && defaultCompanies.length > 0) {
      setIsCompanyPickerOpen(true);
    } else {
      setIsCompanyPickerOpen(false);
    }
  };

  const clearCompanySelection = (): void => {
    onClearSelection();
    setSearchTerm('');
    setIsCompanyPickerOpen(false);
  };

  // Close company picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (companyPickerRef.current && !companyPickerRef.current.contains(event.target as Node)) {
        setIsCompanyPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update search term when selected company changes
  useEffect(() => {
    if (selectedCompany) {
      setSearchTerm(selectedCompany.name || selectedCompany.business_name || '');
    }
  }, [selectedCompany]);

  // Debug logging
  useEffect(() => {
    console.log('🔍 Search state:', {
      searchTerm,
      isCompanyPickerOpen,
      companiesData: companiesData?.length || 0,
      defaultCompanies: defaultCompanies?.length || 0,
      searchError,
      defaultError
    });
  }, [searchTerm, isCompanyPickerOpen, companiesData, defaultCompanies, searchError, defaultError]);

  // Helper function to safely get error message
  const getErrorMessage = (error: any): string => {
    if (error && typeof error === 'object') {
      if (error.message) return error.message;
      if (error.error) return error.error;
      if (error.toString) return error.toString();
    }
    return 'Unknown error';
  };

  return (
    <div className="relative" ref={companyPickerRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoadingCompanies ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          ) : (
            <FaSearch className="h-4 w-4 text-gray-400" />
          )}
        </div>
        <input
          type="text"
          placeholder="Search companies by name or ID..."
          value={searchTerm}
          onChange={(e) => handleCompanySearch(e.target.value)}
          onFocus={() => {
            if (searchTerm.trim().length >= 1 || (defaultCompanies && defaultCompanies.length > 0)) {
              setIsCompanyPickerOpen(true);
            }
          }}
          className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
            selectedCompany 
              ? 'border-blue-500 bg-blue-50 text-blue-900' 
              : searchTerm.trim().length >= 1
              ? 'border-blue-400 bg-blue-50 text-gray-900'
              : 'border-gray-300 bg-white text-gray-900'
          }`}
        />
        {selectedCompany && (
          <button
            onClick={clearCompanySelection}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={() => setIsCompanyPickerOpen(!isCompanyPickerOpen)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
        >
          <FaChevronDown className={`h-4 w-4 transition-transform ${isCompanyPickerOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Company Dropdown */}
      {isCompanyPickerOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-auto">
          {isLoadingCompanies ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
              Searching for companies...
            </div>
          ) : searchError ? (
            <div className="p-4 text-center text-red-500">
              <p>Error searching companies</p>
              <p className="text-sm text-gray-400 mt-1">Please try again</p>
              <p className="text-xs text-red-400 mt-1">Error: {getErrorMessage(searchError)}</p>
            </div>
          ) : companiesData && companiesData.length > 0 ? (
            <div className="py-1">
              <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                Found {companiesData.length} company{companiesData.length !== 1 ? 'ies' : ''}
              </div>
              {companiesData.map((company: Company) => (
                <button
                  key={company.id}
                  onClick={() => handleCompanySelect(company)}
                  className="w-full px-4 py-3 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors"
                >
                  <div className="font-medium text-gray-900">
                    {company.name || company.business_name || `Company ${company.id}`}
                  </div>
                  <div className="text-sm text-gray-500">ID: {company.id}</div>
                </button>
              ))}
            </div>
          ) : searchTerm.trim().length >= 1 && !isLoadingCompanies ? (
            <div className="p-4 text-center text-gray-500">
              <FaSearch className="mx-auto h-8 w-8 text-gray-300 mb-2" />
              <p>No companies found matching "{searchTerm}"</p>
              <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
              {searchError && (
                <p className="text-xs text-red-400 mt-1">Search error: {getErrorMessage(searchError)}</p>
              )}
            </div>
          ) : searchTerm.trim().length === 0 && defaultCompanies && defaultCompanies.length > 0 ? (
            <div className="py-1">
              <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                Popular Companies
              </div>
              {defaultCompanies.slice(0, 10).map((company: Company) => (
                <button
                  key={company.id}
                  onClick={() => handleCompanySelect(company)}
                  className="w-full px-4 py-3 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors"
                >
                  <div className="font-medium text-gray-900">
                    {company.name || company.business_name || `Company ${company.id}`}
                  </div>
                  <div className="text-sm text-gray-500">ID: {company.id}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              <FaSearch className="mx-auto h-8 w-8 text-gray-300 mb-2" />
              <p>Start typing to search companies</p>
              <p className="text-sm text-gray-400 mt-1">Search by company name or ID</p>
              {defaultError && (
                <p className="text-xs text-red-400 mt-1">Error loading companies: {getErrorMessage(defaultError)}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanySearch;
